import { Texture2D } from "./Texture2D";
import { BaseTexture } from "./BaseTexture";
import { RenderTargetFormat } from "../RenderEngine/RenderEnum/RenderTargetFormat";
import { Color } from "../maths/Color";
import { LayaGL } from "../layagl/LayaGL";
import { NotImplementedError } from "../utils/Error";
import { Stat } from "../utils/Stat";
export class RenderTexture2D extends BaseTexture {
    static __init__() {
        RenderTexture2D._empty = new RenderTexture2D(1, 1, RenderTargetFormat.R8G8B8, RenderTargetFormat.None);
        RenderTexture2D._empty.width = 0;
        RenderTexture2D._empty.height = 0;
        RenderTexture2D._empty.lock = true;
    }
    static createFromPool(width, height, colorFormat, depthFormat, multiSampler = 1) {
        for (let i = RenderTexture2D._pool.length - 1; i >= 0; i--) {
            let rt = RenderTexture2D._pool[i];
            if (rt.destroyed) {
                RenderTexture2D._pool.splice(i, 1);
                let gpuMem = rt._renderTarget ? (rt._renderTarget.gpuMemory / 1024 / 1024) : 0;
                RenderTexture2D._poolMemory -= gpuMem;
                RenderTexture2D._poolTimeouts.delete(rt._id);
                continue;
            }
            if (rt.width == width && rt.height == height && rt.getColorFormat() == colorFormat && rt.depthStencilFormat == depthFormat && rt._multiSamples == multiSampler) {
                rt._inPool = false;
                RenderTexture2D._pool.splice(i, 1);
                RenderTexture2D._poolMemory -= (rt._renderTarget.gpuMemory / 1024 / 1024);
                RenderTexture2D._poolTimeouts.delete(rt._id);
                return rt;
            }
        }
        let rt = new RenderTexture2D(width, height, colorFormat, depthFormat, multiSampler);
        rt.lock = true;
        return rt;
    }
    static recoverToPool(rt) {
        if (rt._inPool || rt.destroyed)
            return;
        RenderTexture2D._pool.push(rt);
        RenderTexture2D._poolMemory += (rt._renderTarget.gpuMemory / 1024 / 1024);
        rt._inPool = true;
        RenderTexture2D._poolTimeouts.set(rt.id, performance.now());
        RenderTexture2D._cleanupExcess();
    }
    static clearPool() {
        if (RenderTexture2D._poolMemory < 256) {
            return;
        }
        for (var i in RenderTexture2D._pool) {
            RenderTexture2D._pool[i].destroy();
        }
        RenderTexture2D._pool = [];
        RenderTexture2D._poolMemory = 0;
        RenderTexture2D._poolTimeouts.clear();
    }
    static cleanupExpired() {
        let currentFrame = Stat.loopCount;
        if (currentFrame - RenderTexture2D._lastCleanupFrame < RenderTexture2D.cleanupFrameInterval) {
            return -1;
        }
        RenderTexture2D._lastCleanupFrame = currentFrame;
        let timeout = RenderTexture2D.cleanupTimeout;
        let currentTime = performance.now();
        let cleanedCount = 0;
        for (let i = RenderTexture2D._pool.length - 1; i >= 0; i--) {
            let rt = RenderTexture2D._pool[i];
            let poolTime = RenderTexture2D._poolTimeouts.get(rt.id);
            if (poolTime && (currentTime - poolTime) > timeout) {
                RenderTexture2D._pool.splice(i, 1);
                RenderTexture2D._poolMemory -= (rt._renderTarget.gpuMemory / 1024 / 1024);
                RenderTexture2D._poolTimeouts.delete(rt.id);
                rt.destroy();
                cleanedCount++;
            }
        }
        return cleanedCount;
    }
    static _cleanupExcess() {
        if (RenderTexture2D._poolMemory <= RenderTexture2D.maxPoolMemory) {
            return;
        }
        let objectsWithTime = [];
        for (let i = 0; i < RenderTexture2D._pool.length; i++) {
            let rt = RenderTexture2D._pool[i];
            let poolTime = RenderTexture2D._poolTimeouts.get(rt.id);
            if (poolTime !== undefined) {
                objectsWithTime.push({ rt: rt, time: poolTime });
            }
        }
        objectsWithTime.sort((a, b) => a.time - b.time);
        for (let i = 0; i < objectsWithTime.length; i++) {
            let item = objectsWithTime[i];
            let rt = item.rt;
            if (RenderTexture2D._poolMemory <= RenderTexture2D.maxPoolMemory) {
                break;
            }
            let index = RenderTexture2D._pool.indexOf(rt);
            if (index >= 0) {
                RenderTexture2D._pool.splice(index, 1);
                let gpuMem = rt._renderTarget ? (rt._renderTarget.gpuMemory / 1024 / 1024) : 0;
                RenderTexture2D._poolMemory -= gpuMem;
                RenderTexture2D._poolTimeouts.delete(rt.id);
                rt.destroy();
            }
        }
    }
    static get currentActive() {
        return RenderTexture2D._currentActive;
    }
    get depthStencilFormat() {
        return this._depthStencilFormat;
    }
    get defaultTexture() {
        return Texture2D.grayTexture;
    }
    getIsReady() {
        return true;
    }
    getColorFormat() {
        return this._colorFormat;
    }
    get sourceWidth() {
        return this._width;
    }
    get sourceHeight() {
        return this._height;
    }
    get offsetX() {
        return 0;
    }
    get offsetY() {
        return 0;
    }
    constructor(width, height, format = RenderTargetFormat.R8G8B8, depthStencilFormat = RenderTargetFormat.None, multiSampler = 1) {
        super(width, height, format);
        this._mgrKey = 0;
        this._invertY = false;
        this._inPool = false;
        this._colorFormat = format;
        this._depthStencilFormat = depthStencilFormat;
        this._multiSamples = multiSampler;
        if (width != 0 && height != 0) {
            this._create();
        }
        this.lock = true;
    }
    get isCube() {
        return this._renderTarget._isCube;
    }
    get samples() {
        return this._renderTarget._samples;
    }
    get generateMipmap() {
        return this._renderTarget._generateMipmap;
    }
    _start() {
        throw new NotImplementedError();
    }
    _end() {
        throw new NotImplementedError();
    }
    _create() {
        this._renderTarget = LayaGL.textureContext.createRenderTargetInternal(this.width, this.height, this._colorFormat, this.depthStencilFormat, false, false, this._multiSamples, false);
        if (this._renderTarget._texturesResolve) {
            this._texture = this._renderTarget._texturesResolve[0];
            this._renderTarget._textures[0].gammaCorrection = 2.2;
        }
        else {
            this._texture = this._renderTarget._textures[0];
        }
        this._texture.gammaCorrection = 2.2;
    }
    clear(r = 0.0, g = 0.0, b = 0.0, a = 1.0) {
        RenderTexture2D._clearColor.r = r;
        RenderTexture2D._clearColor.g = g;
        RenderTexture2D._clearColor.b = b;
        RenderTexture2D._clearColor.a = a;
        RenderTexture2D._clear = true;
    }
    getData(x, y, width, height) {
        const pixelCount = width * height * 4;
        let pixelArray;
        switch (this._renderTarget.colorFormat) {
            case RenderTargetFormat.R8G8B8:
            case RenderTargetFormat.R8G8B8A8:
                pixelArray = new Uint8Array(pixelCount);
                break;
            case RenderTargetFormat.R16G16B16A16:
                pixelArray = new Float32Array(pixelCount);
                break;
            default:
                throw new Error("getData is not supported " + this._renderTarget.colorFormat.toString() + "format");
        }
        LayaGL.textureContext.readRenderTargetPixelData(this._renderTarget, x, y, width, height, pixelArray);
        return pixelArray;
    }
    getDataAsync(xOffset, yOffset, width, height, out) {
        return LayaGL.textureContext.readRenderTargetPixelDataAsync(this._renderTarget, xOffset, yOffset, width, height, out);
    }
    recycle() {
    }
    _disposeResource() {
        this._renderTarget && this._renderTarget.dispose();
    }
}
RenderTexture2D._pool = [];
RenderTexture2D._poolMemory = 0;
RenderTexture2D._poolTimeouts = new Map();
RenderTexture2D.cleanupTimeout = 30000;
RenderTexture2D.cleanupFrameInterval = 360;
RenderTexture2D.maxPoolMemory = 128;
RenderTexture2D._lastCleanupFrame = 0;
RenderTexture2D._clearColor = new Color(0, 0, 0, 0);
RenderTexture2D._clear = false;
